import { useMemo } from 'react';
import {
  useForecastComparisonPeriod,
  useForecastPeriod,
  useLatestForecastModel,
  usePreviousForecastWeekId,
  useYtdStartAndEndWeekIdsForForecast,
  useForecastStartAndEndWeekIds,
  useForecastComparisonStartAndEndWeekIds
} from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/hooks';
import { useKeyMetricForecastSummaryData } from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/useKeyMetricForecastSummaryData';
import { useMetricByWeekIdV2, UseMetricByWeekIdV2Args } from 'src/components/BeaconV2Components/SplineChartV2';
import { SplineChartV2Series, SplineChartV2DataPoint } from 'src/components/BeaconV2Components/SplineChartV2';
import {
  alignDataToWeekIds,
  AlignDataToWeekIdsOptions
} from 'src/components/BeaconV2Components/SplineChartV2/splineChartV2Utils';
import { calculateMetricTotal } from 'src/components/BeaconV2Components/SplineChartV2/metricTotalUtils';
import {
  ForecastComparisonPeriod,
  ForecastPeriod
} from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/types';
import { useMetricByWeekIdArgsForForecast } from './useForecastMetricByWeekId';
import { getNextWeekId, getWeekIdRange, timestampToWeekId } from 'src/utils/dateUtils';
import { useMetricFormatter } from 'src/utils/Hooks';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { calculatePercentChange } from 'src/utils/app';

/**
 * Converts V1 forecast data (number[][]) to V2 format (SplineChartV2DataPoint[])
 */
function convertForecastDataToV2(forecastData: number[][]): SplineChartV2DataPoint[] {
  return forecastData.map(([timestamp, value]) => ({
    weekId: timestampToWeekId(timestamp),
    value: value ?? null
  }));
}

export interface UseForecastMetricByWeekIdV2Args
  extends Omit<
    UseMetricByWeekIdV2Args,
    'requestBuilder' | 'primaryAggregationBuilder' | 'secondaryAggregationBuilder'
  > {
  forecastPeriodOverride?: ForecastPeriod;
  useKeyMetricForecastSummaryDataArgs?: Parameters<typeof useKeyMetricForecastSummaryData>[0];
  /**
   * Override the aggregation function used for calculating totals.
   * For example, 'avg' for contentScore which should be averaged, not summed.
   */
  aggregationFunctionForTotal?: string;
}

export interface UseForecastMetricByWeekIdV2Result {
  // Series data ready for SplineChartV2
  primarySeries: SplineChartV2Series;
  comparisonSeries: SplineChartV2Series;
  forecastSeries: SplineChartV2Series;

  // Raw data points
  primaryData: SplineChartV2DataPoint[];
  comparisonData: SplineChartV2DataPoint[];
  forecastData: SplineChartV2DataPoint[];

  // Formatted totals
  formattedPrimaryTotal: string;
  formattedComparisonTotal: string;
  formattedPercentChange: string;

  // Raw values
  percentChange: number;

  // Loading state
  isLoading: boolean;
}

/**
 * V2 version of useForecastMetricByWeekId that returns data in SplineChartV2 format.
 * Combines YTD/historic data with forecast projections.
 */
export default function useForecastMetricByWeekIdV2({
  useKeyMetricForecastSummaryDataArgs,
  forecastPeriodOverride,
  aggregationFunctionForTotal,
  ...args
}: UseForecastMetricByWeekIdV2Args): UseForecastMetricByWeekIdV2Result {
  const { loading: modelLoading } = useLatestForecastModel();
  const { getSplineData, isLoading: forecastLoading } = useKeyMetricForecastSummaryData(
    useKeyMetricForecastSummaryDataArgs
  );
  const forecastPeriod = useForecastPeriod();
  const formatMetric = useMetricFormatter();
  const previousForecastWeekId = usePreviousForecastWeekId();
  const { startWeekId: ytdStartWeekId, endWeekId: ytdEndWeekId } = useYtdStartAndEndWeekIdsForForecast();
  const { startWeekId: forecastStartWeekId, endWeekId: forecastEndWeekId } = useForecastStartAndEndWeekIds();
  // Time API: Prior Year comparison start/end come from getComparisonWeekId (hooks.ts).
  // Metric request uses these via useMetricByWeekIdArgsForForecast (comparison range filter).
  const {
    startWeekId: comparisonStartWeekId,
    endWeekId: comparisonEndWeekId,
    isLoading: comparisonWeeksLoading
  } = useForecastComparisonStartAndEndWeekIds();
  const forecastComparison = useForecastComparisonPeriod();

  // Get the request builder args for forecast time periods
  const metricByWeekIdArgs = useMetricByWeekIdArgsForForecast();

  // Determine the aggregation function override (e.g., 'avg' for contentScore)
  const effectiveAggregationFunctionOverride =
    aggregationFunctionForTotal ?? (args.fieldName === 'contentScore' ? 'avg' : undefined);

  // For Prior Year we must wait for the time API to return comparison start/end before sending the metric request.
  const shouldPerformMetricFetch =
    !modelLoading && (forecastComparison !== ForecastComparisonPeriod.PRIOR_YEAR || !comparisonWeeksLoading);

  // Use the V2 hook to get primary and comparison data
  // Skip data alignment because forecasts use a different week range than Redux mainTimePeriod
  // The forecast YTD range comes from useYtdStartAndEndWeekIdsForForecast, not Redux
  const metricByValuesV2 = useMetricByWeekIdV2({
    ...metricByWeekIdArgs,
    ...args,
    skipDataAlignment: true,
    useGenericAdvancedSearchArgs: {
      shouldPerformFetch: shouldPerformMetricFetch,
      ...args.useGenericAdvancedSearchArgs
    }
  });

  // Convert raw forecast data to V2 format
  const rawForecastData = useMemo(() => {
    const splineData = getSplineData(args.indexName, args.fieldName);
    return convertForecastDataToV2(splineData);
  }, [getSplineData, args.indexName, args.fieldName]);

  // Trim forecast to selected period
  const forecastDataInRange = useMemo(() => {
    if (!forecastStartWeekId || !forecastEndWeekId) return rawForecastData;
    return rawForecastData
      .filter((p) => p.weekId >= forecastStartWeekId && p.weekId <= forecastEndWeekId)
      .sort((a, b) => a.weekId - b.weekId);
  }, [rawForecastData, forecastStartWeekId, forecastEndWeekId]);

  const effectiveForecastPeriod = forecastPeriodOverride ?? forecastPeriod;

  // Same as useMetricByWeekIdV2: pad missing weeks and null values with 0 so comparison aligns to primary (no start shift).
  const alignmentOptions: AlignDataToWeekIdsOptions = useMemo(() => ({ fillNullWithZero: true }), []);

  // Align primary to full YTD range so data spans the entire chart; fill missing weeks with 0 (not null).
  const ytdRangeWeekIds = useMemo(
    () => (ytdStartWeekId && ytdEndWeekId ? getWeekIdRange(ytdStartWeekId, ytdEndWeekId) : []),
    [ytdStartWeekId, ytdEndWeekId]
  );
  const primaryData = useMemo(() => {
    if (effectiveForecastPeriod !== ForecastPeriod.FULL_YEAR) return [];
    if (ytdRangeWeekIds.length === 0) return metricByValuesV2.primaryData;
    return alignDataToWeekIds(metricByValuesV2.primaryData, ytdRangeWeekIds, alignmentOptions);
  }, [effectiveForecastPeriod, metricByValuesV2.primaryData, ytdRangeWeekIds, alignmentOptions]);

  // Matches V1 useDisplayedForecastData: prepend the last point from the "previous" series
  // so the forecast line connects smoothly to whichever series precedes it.
  const displayedForecastData = useMemo(() => {
    if (effectiveForecastPeriod === ForecastPeriod.FULL_YEAR && primaryData.length > 0) {
      return [primaryData[primaryData.length - 1], ...forecastDataInRange];
    }
    if (forecastComparison === ForecastComparisonPeriod.PRIOR_PERIOD && metricByValuesV2.comparisonData.length > 0) {
      return [metricByValuesV2.comparisonData[metricByValuesV2.comparisonData.length - 1], ...forecastDataInRange];
    }
    return forecastDataInRange;
  }, [effectiveForecastPeriod, forecastComparison, primaryData, metricByValuesV2.comparisonData, forecastDataInRange]);

  // Category week IDs = chart x-axis range. comparisonStartWeekId/End come from time API (Prior Year) or local (Prior Period).
  const weekIdsForCategories = useMemo(() => {
    if (effectiveForecastPeriod === ForecastPeriod.FULL_YEAR && primaryData.length > 0) {
      const primaryWeekIds = primaryData.map((p) => p.weekId);
      const forecastTail = displayedForecastData.length > 1 ? displayedForecastData.slice(1).map((p) => p.weekId) : [];
      return [...primaryWeekIds, ...forecastTail];
    }
    // Non-FULL_YEAR: comparison range from API (Prior Year) or getPriorPeriodWeekId (Prior Period).
    if (comparisonStartWeekId && comparisonEndWeekId) {
      const comparisonRangeWeekIds = getWeekIdRange(comparisonStartWeekId, comparisonEndWeekId);
      const useAllForecastWeeks = forecastComparison === ForecastComparisonPeriod.PRIOR_PERIOD;
      const forecastTail =
        displayedForecastData.length > 0
          ? (useAllForecastWeeks ? displayedForecastData : displayedForecastData.slice(1)).map((p) => p.weekId)
          : [];
      return [...comparisonRangeWeekIds, ...forecastTail];
    }
    const rawComparisonData = metricByValuesV2.comparisonData;
    const primaryPortion = primaryData.length > 0 ? primaryData : rawComparisonData;
    const useAllForecastWeeks = forecastComparison === ForecastComparisonPeriod.PRIOR_PERIOD;
    const forecastPortion =
      displayedForecastData.length > 0
        ? useAllForecastWeeks
          ? displayedForecastData
          : displayedForecastData.length > 1
          ? displayedForecastData.slice(1)
          : []
        : [];
    return [...primaryPortion.map((p) => p.weekId), ...forecastPortion.map((p) => p.weekId)];
  }, [
    effectiveForecastPeriod,
    forecastComparison,
    primaryData,
    displayedForecastData,
    comparisonStartWeekId,
    comparisonEndWeekId,
    metricByValuesV2.comparisonData
  ]);

  // Align comparison to match useMetricByWeekIdV2: use "comparison week IDs for main period" so data is connected 1:1.
  // Comparison weekIds stay as prior year/period (no shifting to current year); meta is derived from point.weekId in chart.
  const alignedComparisonData = useMemo(() => {
    const rawComparisonData = metricByValuesV2.comparisonData;

    // For Prior Period, skip alignment - use raw comparison data as-is.
    // The comparison range is already correctly fetched via useMetricByWeekIdArgsForForecast.
    if (
      forecastComparison === ForecastComparisonPeriod.PRIOR_PERIOD &&
      effectiveForecastPeriod !== ForecastPeriod.FULL_YEAR
    ) {
      return rawComparisonData;
    }

    // For Prior Year (including FULL_YEAR), use the time API range for accurate calendar alignment.
    // The API accounts for year boundary differences (e.g., 53-week years vs 52-week years).
    if (comparisonStartWeekId != null && comparisonEndWeekId != null) {
      const comparisonWeekIds = getWeekIdRange(comparisonStartWeekId, comparisonEndWeekId);
      if (comparisonWeekIds.length === 0) return rawComparisonData;
      return alignDataToWeekIds(rawComparisonData, comparisonWeekIds, alignmentOptions);
    }

    return rawComparisonData;
  }, [
    effectiveForecastPeriod,
    metricByValuesV2.comparisonData,
    forecastComparison,
    comparisonStartWeekId,
    comparisonEndWeekId,
    alignmentOptions
  ]);

  // Calculate totals for forecast periods
  // For FULL_YEAR: combined total (actual YTD + forecast) vs full comparison year
  // For non-FULL_YEAR: forecast total vs full comparison data (no filtering by week IDs)
  const { formattedPrimaryTotal, formattedComparisonTotal, percentChange, formattedPercentChange } = useMemo(() => {
    const isPercentType = metricByValuesV2.metricType === METRICTYPE.PERCENT;

    if (effectiveForecastPeriod === ForecastPeriod.FULL_YEAR) {
      // Combine actual YTD data with forecast data (trimmed to end of year) for total calculation
      // This properly handles different aggregation types (sum, avg, stats, cardinality, lastValue)
      const combinedData = [...metricByValuesV2.primaryData, ...forecastDataInRange];
      const combinedTotal = calculateMetricTotal(combinedData, metricByValuesV2.field, {
        aggregationFunctionOverride: effectiveAggregationFunctionOverride
      });

      // For forecasts, calculate comparison total from ALL comparison data (full prior year)
      // not the filtered subset that useMetricByWeekIdV2 returns
      const fullComparisonTotal = calculateMetricTotal(metricByValuesV2.comparisonData, metricByValuesV2.field, {
        aggregationFunctionOverride: effectiveAggregationFunctionOverride
      });

      const pctChange = calculatePercentChange(combinedTotal, fullComparisonTotal);

      const formattedTotal = formatMetric(combinedTotal, metricByValuesV2.metricType, {
        showFullValue: isPercentType,
        decimalPlaces: 2
      });

      const formattedComparison = formatMetric(fullComparisonTotal, metricByValuesV2.metricType, {
        showFullValue: isPercentType,
        decimalPlaces: 2
      });

      const formattedPctChange = formatMetric(Math.abs(pctChange), METRICTYPE.PERCENT, { decimalPlaces: 1 });

      return {
        formattedPrimaryTotal: formattedTotal,
        formattedComparisonTotal: formattedComparison,
        percentChange: pctChange,
        formattedPercentChange: formattedPctChange
      };
    }

    // For non-FULL_YEAR periods (Next 52W, Next 26W, etc.):
    // - Primary total is from FORECAST data only (trimmed to selected period)
    // - Comparison total is from ALL comparison data (no week ID filtering)
    // This matches V1 behavior with buildDataSetForMetricTotal and customFilterComparisonForTotal
    const forecastTotal = calculateMetricTotal(forecastDataInRange, metricByValuesV2.field, {
      aggregationFunctionOverride: effectiveAggregationFunctionOverride
    });

    // Calculate comparison total from ALL comparison data
    // Don't filter by week IDs - use the full comparison period that was fetched
    const fullComparisonTotal = calculateMetricTotal(metricByValuesV2.comparisonData, metricByValuesV2.field, {
      aggregationFunctionOverride: effectiveAggregationFunctionOverride
    });

    const pctChange = calculatePercentChange(forecastTotal, fullComparisonTotal);

    const formattedTotal = formatMetric(forecastTotal, metricByValuesV2.metricType, {
      showFullValue: isPercentType,
      decimalPlaces: 2
    });

    const formattedComparison = formatMetric(fullComparisonTotal, metricByValuesV2.metricType, {
      showFullValue: isPercentType,
      decimalPlaces: 2
    });

    const formattedPctChange = formatMetric(Math.abs(pctChange), METRICTYPE.PERCENT, { decimalPlaces: 1 });

    return {
      formattedPrimaryTotal: formattedTotal,
      formattedComparisonTotal: formattedComparison,
      percentChange: pctChange,
      formattedPercentChange: formattedPctChange
    };
  }, [
    effectiveForecastPeriod,
    effectiveAggregationFunctionOverride,
    metricByValuesV2.primaryData,
    metricByValuesV2.comparisonData,
    metricByValuesV2.field,
    metricByValuesV2.metricType,
    forecastDataInRange,
    formatMetric
  ]);

  // Build series for SplineChartV2
  const primarySeries: SplineChartV2Series = useMemo(
    () => ({
      id: `${args.indexName}-${args.fieldName}-primary`,
      name: 'Primary',
      data: primaryData
    }),
    [args.indexName, args.fieldName, primaryData]
  );

  const comparisonSeries: SplineChartV2Series = useMemo(
    () => ({
      id: `${args.indexName}-${args.fieldName}-comparison`,
      name: 'Comparison',
      data: alignedComparisonData
    }),
    [args.indexName, args.fieldName, alignedComparisonData]
  );

  const forecastSeries: SplineChartV2Series = useMemo(
    () => ({
      id: `${args.indexName}-${args.fieldName}-forecast`,
      name: 'Forecast',
      data: displayedForecastData,
      isDashed: true
    }),
    [args.indexName, args.fieldName, displayedForecastData]
  );

  return {
    primarySeries,
    comparisonSeries,
    forecastSeries,
    primaryData,
    comparisonData: alignedComparisonData,
    forecastData: displayedForecastData,
    formattedPrimaryTotal,
    formattedComparisonTotal,
    formattedPercentChange,
    percentChange,
    isLoading:
      modelLoading ||
      forecastLoading ||
      metricByValuesV2.isLoading ||
      (forecastComparison === ForecastComparisonPeriod.PRIOR_YEAR && comparisonWeeksLoading)
  };
}
